import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Play, Calendar, Users, ArrowRight, CheckCircle2 } from 'lucide-react';

export const Route = createFileRoute('/demo')({
  component: DemoPage,
});

const demoOptions = [
  { icon: Play, title: 'Watch Demo Video', description: 'See ForgeNetwork in action with a 5-minute walkthrough.', action: 'Watch Now', href: '#video' },
  { icon: Calendar, title: 'Schedule a Call', description: 'Get a personalized demo with our product team.', action: 'Book Time', href: '/contact', isLink: true },
  { icon: Users, title: 'Free Trial', description: 'Try ForgeNetwork free for 14 days, no credit card required.', action: 'Start Free', href: '/register', isLink: true },
];

const demoFeatures = [
  'Upload and analyze a repository in under 60 seconds',
  'Automatically extract features and generate documentation',
  'Identify security vulnerabilities with detailed remediation steps',
  'Export comprehensive PRDs in Markdown or JSON',
  'Collaborate with your team in real-time',
];

function DemoPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Demo</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">See ForgeNetwork in Action</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover how ForgeNetwork transforms the way you understand and document code.
            </p>
          </div>
          <section className="mb-24">
            <div className="grid gap-6 md:grid-cols-3 max-w-4xl mx-auto">
              {demoOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Card key={option.title} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="text-center">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-lg">{option.title}</CardTitle>
                      <CardDescription>{option.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      {option.isLink ? (
                        <Button asChild>
                          <Link to={option.href as '/contact' | '/register'}>{option.action}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                      ) : (
                        <Button variant="outline">
                          <a href={option.href}>{option.action}<ArrowRight className="ml-2 h-4 w-4" /></a>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
          <section id="video" className="mb-24">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Product Walkthrough</h2>
              <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg border overflow-hidden">
                {/* Interactive product preview */}
                <div className="w-full h-full p-6 flex flex-col">
                  {/* Header bar */}
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-700/50">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      <div className="h-3 w-3 rounded-full bg-yellow-500" />
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                    </div>
                    <div className="text-sm text-slate-400">forgenetwork.pro/dashboard</div>
                  </div>
                  {/* Dashboard content */}
                  <div className="flex-1 grid grid-cols-4 gap-4 pt-4">
                    {/* Sidebar */}
                    <div className="space-y-2">
                      <div className="h-8 bg-primary/20 rounded flex items-center px-3"><span className="text-xs text-primary font-medium">Dashboard</span></div>
                      <div className="h-8 bg-slate-700/30 rounded" />
                      <div className="h-8 bg-slate-700/30 rounded" />
                      <div className="h-8 bg-slate-700/30 rounded" />
                    </div>
                    {/* Main content */}
                    <div className="col-span-3 space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                          <div className="text-xs text-slate-400 mb-1">Repositories</div>
                          <div className="text-2xl font-bold text-white">12</div>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                          <div className="text-xs text-slate-400 mb-1">Features Extracted</div>
                          <div className="text-2xl font-bold text-emerald-400">847</div>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                          <div className="text-xs text-slate-400 mb-1">PRDs Generated</div>
                          <div className="text-2xl font-bold text-blue-400">34</div>
                        </div>
                      </div>
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30 flex-1">
                        <div className="text-sm text-slate-300 mb-3">Recent Analysis</div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3"><div className="h-2 w-2 rounded-full bg-emerald-400" /><div className="h-3 w-3/4 bg-slate-700/50 rounded" /></div>
                          <div className="flex items-center gap-3"><div className="h-2 w-2 rounded-full bg-emerald-400" /><div className="h-3 w-2/3 bg-slate-700/50 rounded" /></div>
                          <div className="flex items-center gap-3"><div className="h-2 w-2 rounded-full bg-amber-400" /><div className="h-3 w-1/2 bg-slate-700/50 rounded" /></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">Start your free trial to experience the full platform</p>
            </div>
          </section>
          <section className="mb-24">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">What You'll See</h2>
              <div className="space-y-4">
                {demoFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section>
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20 max-w-2xl mx-auto">
              <CardContent className="py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
                <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                  Join thousands of developers already using ForgeNetwork to understand and document their code.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button size="lg" asChild>
                    <Link to="/register">Start Free Trial<ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/pricing">View Pricing</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
